import { motion, AnimatePresence } from "framer-motion";
import { DynamicField, type FieldConfig } from "./DynamicField";

interface StepConfig {
  id: string;
  name: string;
  description?: string | null;
  helper_text?: string | null;
}

interface WizardStepRendererProps {
  step: StepConfig;
  fields: FieldConfig[];
  formData: Record<string, unknown>;
  errors: Record<string, string>;
  onChange: (key: string, value: unknown) => void;
  direction: number;
  dealId?: string | null;
}

export function WizardStepRenderer({
  step,
  fields,
  formData,
  errors,
  onChange,
  direction,
  dealId,
}: WizardStepRendererProps) {
  // Group fields by category
  const grouped = new Map<string, FieldConfig[]>();
  fields.forEach((f) => {
    const cat = f.category || "";
    if (!grouped.has(cat)) grouped.set(cat, []);
    grouped.get(cat)!.push(f);
  });

  return (
    <AnimatePresence mode="wait" custom={direction}>
      <motion.div
        key={step.id}
        custom={direction}
        initial={{ opacity: 0, x: direction * 40 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: direction * -40 }}
        transition={{ duration: 0.25, ease: "easeInOut" }}
      >
        {/* Step header */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground">{step.name}</h2>
          {step.description && (
            <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
          )}
          {step.helper_text && (
            <p className="text-xs text-muted-foreground/70 mt-1 italic">{step.helper_text}</p>
          )}
        </div>

        {/* Fields by category */}
        <div className="space-y-8">
          {[...grouped.entries()].map(([category, catFields]) => (
            <div key={category || "__default"}>
              {category && (
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 border-b border-border pb-2">
                  {category}
                </h3>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {catFields.map((field) => (
                  <div
                    key={field.id}
                    className={
                      field.field_type === "textarea" ||
                      field.field_type === "multi_select" ||
                      field.field_type === "file_upload"
                        ? "sm:col-span-2"
                        : ""
                    }
                  >
                    <DynamicField
                      field={field}
                      value={formData[field.field_key]}
                      onChange={onChange}
                      error={errors[field.field_key]}
                      dealId={dealId}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
